import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * User Service - Handles all operations related to users (both brands and influencers)
 */
const userService = {
  /**
   * Create a new user in Firestore
   * @param {string} uid - User ID from Firebase Auth
   * @param {object} userData - User data to be stored
   * @returns {Promise} - Promise that resolves when user is created
   */
  createUser: async (uid, userData) => {
    try {
      const userRef = doc(db, 'users', uid);
      
      // Set default fields based on user type
      const defaultData = {
        uid,
        email: userData.email || '',
        displayName: userData.displayName || '',
        userType: userData.userType || 'influencer',
        photoURL: userData.photoURL || '',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      };
      
      // Add type-specific fields
      if (userData.userType === 'brand') {
        defaultData.brandDetails = {
          companyName: userData.companyName || '',
          industry: userData.industry || '',
          website: userData.website || '',
          location: userData.location || '',
          description: userData.description || '',
          logo: userData.logo || ''
        };
        defaultData.influencerDetails = null;
      } else {
        defaultData.influencerDetails = {
          bio: userData.bio || '',
          location: userData.location || '',
          categories: userData.categories || [],
          followers: userData.followers || {
            instagram: 0,
            tiktok: 0,
            youtube: 0
          },
          engagementRate: userData.engagementRate || 0,
          socialAccounts: userData.socialAccounts || {
            instagram: '',
            tiktok: '',
            youtube: ''
          }
        };
        defaultData.brandDetails = null;
      }
      
      // Merge with provided data
      const dataToSave = { ...defaultData, ...userData };
      
      // Remove any undefined fields
      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === undefined) {
          delete dataToSave[key];
        }
      });
      
      await setDoc(userRef, dataToSave);
      return { uid, ...dataToSave };
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  /**
   * Get user by ID
   * @param {string} uid - User ID
   * @returns {Promise<object|null>} - User data or null if not found
   */
  getUserById: async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   * @param {string} uid - User ID
   * @param {object} userData - User data to update
   * @returns {Promise} - Promise that resolves when user is updated
   */
  updateUser: async (uid, userData) => {
    try {
      const userRef = doc(db, 'users', uid);
      
      // Add updated timestamp
      userData.updatedAt = serverTimestamp();
      
      await updateDoc(userRef, userData);
      return { uid, ...userData };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  /**
   * Update user login timestamp
   * @param {string} uid - User ID
   * @returns {Promise} - Promise that resolves when timestamp is updated
   */
  updateLoginTimestamp: async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        lastLogin: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating login timestamp:', error);
      throw error;
    }
  },

  /**
   * Get all brands
   * @returns {Promise<Array>} - Array of brand users
   */
  getAllBrands: async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('userType', '==', 'brand'));
      const querySnapshot = await getDocs(q);
      
      const brands = [];
      querySnapshot.forEach((doc) => {
        brands.push({ id: doc.id, ...doc.data() });
      });
      
      return brands;
    } catch (error) {
      console.error('Error getting brands:', error);
      throw error;
    }
  },

  /**
   * Get all influencers
   * @param {object} filters - Optional filters (categories, minFollowers, etc.)
   * @returns {Promise<Array>} - Array of influencer users
   */
  getAllInfluencers: async (filters = {}) => {
    try {
      const usersRef = collection(db, 'users');
      let q = query(usersRef, where('userType', '==', 'influencer'));
      
      // Apply additional filters if provided
      // Note: This is a simplified approach. For complex filtering,
      // you might need to use composite indexes or multiple queries.
      
      const querySnapshot = await getDocs(q);
      
      // Use a Map to ensure unique influencers by ID
      const influencersMap = new Map();
      
      querySnapshot.forEach((doc) => {
        // Only add if this ID isn't already in the map
        if (!influencersMap.has(doc.id)) {
          influencersMap.set(doc.id, { id: doc.id, ...doc.data() });
        } else {

        }
      });
      
      // Convert map values to array
      let influencers = Array.from(influencersMap.values());

      // Filter out influencers who have opted out of the directory via privacy settings
      // Default to visible if setting is not explicitly set to false
      influencers = influencers.filter(influencer => {
        return influencer.settings?.privacy?.showInDirectory !== false;
      });

      // Apply client-side filtering for more complex filters
      if (filters.categories && filters.categories.length > 0) {
        influencers = influencers.filter(influencer => {
          return influencer.influencerDetails?.categories?.some(category => 
            filters.categories.includes(category)
          );
        });
      }
      
      if (filters.minFollowers) {
        influencers = influencers.filter(influencer => {
          const totalFollowers = Object.values(influencer.influencerDetails?.followers || {})
            .reduce((sum, count) => sum + (Number(count) || 0), 0);
          return totalFollowers >= filters.minFollowers;
        });
      }
      
      return influencers;
    } catch (error) {
      console.error('Error getting influencers:', error);
      throw error;
    }
  },

  /**
   * Check if an influencer profile is complete with all required fields
   * @param {object} profile - User profile data
   * @returns {object} - Object with isComplete flag and missing fields array
   */
  isProfileComplete: (profile) => {
    if (!profile) return { isComplete: false, missingFields: ['Complete profile information'] };
    
    const missingFields = [];
    
    // Check required basic fields
    if (!profile.displayName) missingFields.push('Display Name');
    if (!profile.bio) missingFields.push('Bio');
    if (!profile.location) missingFields.push('Location');
    if (!profile.phone) missingFields.push('Phone Number');
    if (!profile.gender) missingFields.push('Gender');
    if (!profile.age) missingFields.push('Age Range');
    
    // Check mandatory photos
    if (!profile.profileImageUrl && !profile.photoURL) {
      missingFields.push('Profile Photo');
    }
    if (!profile.coverImageUrl) {
      missingFields.push('Cover Photo');
    }
    
    // Check if at least one category is selected
    if (!profile.categories || profile.categories.length === 0) {
      missingFields.push('Content Categories');
    }
    
    // Check social media platforms and their follower counts
    const socialMedia = profile.socialMedia || {};
    const followers = profile.followers || {};
    
    let hasSocialMediaWithFollowers = false;
    
    // Check each platform - if URL exists, follower count must also exist
    const platforms = ['instagram', 'tiktok', 'twitter', 'facebook'];
    const socialMediaIssues = [];
    
    platforms.forEach(platform => {
      if (socialMedia[platform]) {
        // Platform URL exists, check if follower count is provided
        if (!followers[platform] || followers[platform] <= 0) {
          socialMediaIssues.push(`${platform.charAt(0).toUpperCase() + platform.slice(1)} follower count`);
        } else {
          hasSocialMediaWithFollowers = true;
        }
      }
    });
    
    // Must have at least one social media platform with URL and follower count
    if (!hasSocialMediaWithFollowers) {
      missingFields.push('At least one social media profile with follower count');
    }
    
    // Add specific follower count issues
    if (socialMediaIssues.length > 0) {
      missingFields.push(...socialMediaIssues);
    }
    
    return {
      isComplete: missingFields.length === 0,
      missingFields
    };
  }
};

export default userService;
